import React, { useState } from 'react';
import { RefreshCw, Settings, SlidersHorizontal } from 'lucide-react';
import PeerScoringDetailDrawer from '../components/PeerScoringDetailDrawer';
import { usePeerReviewCorrection } from '../data/peerReviewCorrectionStore';
import { peerScoreDetailStudent } from '../data/peerReviewDetailData';

// 恶意打分记录页面
// 页面结构：条件筛选卡片 + 结果卡片 + 右侧打分明细抽屉
export default function MaliciousScoringPage() {
  // ================= 页面状态 =================
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isWangQiangMalicious, setIsWangQiangMalicious] = usePeerReviewCorrection();

  // ================= 表格数据 =================
  const maliciousScoreList = [
    {
      id: 1,
      batch: peerScoreDetailStudent.batch,
      college: peerScoreDetailStudent.college,
      className: peerScoreDetailStudent.className,
      name: peerScoreDetailStudent.name,
      studentId: peerScoreDetailStudent.studentId,
      totalCount: 15,
      lowScoreCount: peerScoreDetailStudent.lowScoreCount,
      lowScoreRate: peerScoreDetailStudent.lowScoreRate,
      status: isWangQiangMalicious ? '是（已作废）' : '否（人工修正）',
    },
    {
      id: 2,
      batch: '2025秋季批次',
      college: '计算机学院',
      className: '计算机3班',
      name: '刘洋',
      studentId: '2102088',
      totalCount: 14,
      lowScoreCount: 8,
      lowScoreRate: '57.14%',
      status: '是（已作废）',
    },
  ];

  return (
    <div className="animate-fade-in space-y-4">
      {/* 条件筛选卡片：定位某批次、某班级、某学生的异常打分记录 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-x-8 gap-y-5">
          <FilterSelect label="学年" />
          <FilterSelect label="学期" />
          <FilterSelect label="批次" />
          <FilterSelect label="学院" />
          <FilterSelect label="班级" />
          <FilterSelect label="打分比例是否超过阈值" />
          <FilterSelect label="是否辅导员恶意打分修正" />
          <FilterInput
            label="学号"
            placeholder="请输入学号"
          />
          <FilterInput
            label="姓名"
            placeholder="请输入姓名"
          />
        </div>

        {/* 查询按钮区 */}
        <div className="flex justify-end gap-3 mt-6">
          <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
            重置
          </button>
          <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
            查询
          </button>
        </div>
      </div>

      {/* 结果卡片：恶意打分记录列表 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        {/* 表格上方操作按钮区 */}
        <div className="flex justify-between items-center mb-4">
          <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
            导出列表
          </button>

          <TableToolbar />
        </div>

        {/* 恶意打分记录表格 */}
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <TableHead>序号</TableHead>
                <TableHead>批次</TableHead>
                <TableHead>学院</TableHead>
                <TableHead>班级</TableHead>
                <TableHead>评分学生姓名</TableHead>
                <TableHead>评分学生学号</TableHead>
                <TableHead>评价学生总数</TableHead>
                <TableHead>恶意打分数量</TableHead>
                <TableHead>恶意打分比例</TableHead>
                <TableHead>是否计入恶意打分</TableHead>
                <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {maliciousScoreList.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.batch}</TableCell>
                  <TableCell>{row.college}</TableCell>
                  <TableCell>{row.className}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.studentId}</TableCell>
                  <TableCell>{row.totalCount}</TableCell>
                  <TableCell>{row.lowScoreCount}</TableCell>
                  <TableCell>{row.lowScoreRate}</TableCell>
                  <TableCell>
                    <span className={row.status.includes('是') ? 'text-red-500' : 'text-orange-500'}>
                      {row.status}
                    </span>
                  </TableCell>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                    <button
                      onClick={() => setIsDetailOpen(true)}
                      className="text-[#1890ff] hover:underline"
                    >
                      查看明细
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 右侧抽屉：恶意打分记录查看明细，只展示 TA 的打分情况 */}
      {isDetailOpen && (
        <PeerScoringDetailDrawer
          onClose={() => setIsDetailOpen(false)}
          showReceived={false}
          title="恶意打分记录明细"
          isMaliciousOverride={isWangQiangMalicious}
          onMaliciousChange={setIsWangQiangMalicious}
        />
      )}
    </div>
  );
}

// 条件筛选：输入框
function FilterInput({ label, placeholder = '' }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-800 shrink-0 whitespace-nowrap">
        {label}：
      </span>
      <input
        placeholder={placeholder}
        className="border border-gray-300 rounded px-3 py-2 text-sm flex-1 min-w-0 outline-none focus:border-[#1890ff]"
      />
    </div>
  );
}

// 条件筛选：下拉框
function FilterSelect({ label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-800 shrink-0 whitespace-nowrap">
        {label}：
      </span>
      <select className="border border-gray-300 rounded px-3 py-2 text-sm flex-1 min-w-0 outline-none focus:border-[#1890ff] bg-white text-gray-500">
        <option>全部</option>
      </select>
    </div>
  );
}

// 表格工具按钮
function TableToolbar() {
  return (
    <div className="flex items-center gap-4 text-gray-700">
      <button className="hover:text-[#1890ff]" title="刷新">
        <RefreshCw size={18} />
      </button>
      <button className="hover:text-[#1890ff]" title="密度">
        <SlidersHorizontal size={18} />
      </button>
      <button className="hover:text-[#1890ff]" title="设置">
        <Settings size={18} />
      </button>
    </div>
  );
}

// 表格表头
function TableHead({ children }) {
  return (
    <th className="py-3 px-4 font-medium text-gray-800">
      {children}
    </th>
  );
}

// 表格单元格
function TableCell({ children }) {
  return (
    <td className="py-3 px-4">
      {children}
    </td>
  );
}
